import { useEffect, useState } from "react";
import { P, pub } from "@/lib/publicApi";
import { ShieldAlert, ExternalLink, Fingerprint } from "lucide-react";

// Public-facing Sex Offender Registry gateway: disclaimer gate, then the official
// public registry portals (NSOPW + state registries — the authoritative sources).
export default function OffenderRegistry() {
  const [agreed, setAgreed] = useState(sessionStorage.getItem("pub_sor_agreed") === "1");
  const [chk, setChk] = useState(false);
  const [regs, setRegs] = useState([]);
  useEffect(() => {
    if (agreed) pub.get("/offender-registries").then((r) => setRegs(r.data.registries)).catch(() => {});
  }, [agreed]);

  if (!agreed) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="pub-sor-disclaimer">
        <div className="flex items-start gap-2.5 mb-4">
          <ShieldAlert size={24} className="mt-0.5 shrink-0" style={{ color: P.gold }} />
          <div>
            <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Sex Offender Registry — Disclaimer</h2>
            <p className="text-sm mt-1" style={{ color: P.dim }}>Welcome to the Sex Offender Registry Search. Please read and agree to the following before proceeding:</p>
          </div>
        </div>
        <div className="border-2 rounded-sm bg-white p-5 space-y-3 text-sm leading-relaxed" style={{ borderColor: P.line, color: P.ink }}>
          <p><b>Purpose:</b> This tool links to publicly available sex offender registry data from NSOPW.gov, the Maryland DPSCS registry, and other state registries. Information is provided solely for public safety awareness.</p>
          <p><b>Accuracy:</b> Registry information is self-reported by registrants and may be time-sensitive. The data cannot be guaranteed to be accurate, complete, or current. Registrants may have moved without notifying the Department.</p>
          <p><b>Prohibited Use:</b> This information shall <b style={{ color: P.red }}>NOT</b> be used to injure, harass, or commit any criminal act against any individual named in the registry or residing at a reported address. Such actions may subject you to criminal prosecution.</p>
          <p><b>Identification:</b> A name alone is insufficient to positively identify a registrant. Positive identification requires fingerprint verification.</p>
          <p><b>Reporting Violations:</b> If you believe a registrant is not complying with registration requirements, contact your local law enforcement agency or the Maryland DPSCS.</p>
          <p className="font-bold" style={{ color: "#7A5A0E" }}>By clicking "I Agree &amp; Continue", you acknowledge that you have read and understand these terms and agree to use this information only for lawful purposes.</p>
        </div>
        <label className="flex items-center gap-2.5 mt-4 text-sm cursor-pointer" style={{ color: P.ink }}>
          <input data-testid="pub-sor-agree-check" type="checkbox" checked={chk} onChange={(e) => setChk(e.target.checked)} className="w-4 h-4" />
          I have read and agree to the above terms and conditions
        </label>
        <button data-testid="pub-sor-agree-btn" disabled={!chk}
          onClick={() => { sessionStorage.setItem("pub_sor_agreed", "1"); setAgreed(true); }}
          className="w-full mt-4 py-3 font-bold text-sm text-white rounded-sm disabled:opacity-50"
          style={{ background: P.gold }}>
          I Agree &amp; Continue to Registry Search
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="pub-sor-portals">
      <div>
        <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Sex Offender Registry Search</h2>
        <p className="text-sm mt-1" style={{ color: P.dim }}>
          These are the official public registries (national + state). Open a portal below and run your name or
          address search directly at the authoritative source — always the most current data available.
        </p>
      </div>
      <div className="space-y-2.5">
        {regs.map((r) => (
          <div key={r.url} data-testid={`pub-sor-registry-${r.region.toLowerCase()}`}
            className="border rounded-sm bg-white p-4 flex items-center gap-3" style={{ borderColor: P.line }}>
            <span className="text-[10px] font-bold px-2 py-1 rounded-sm shrink-0"
              style={{ background: r.region === "NATIONAL" ? "#FBF6E9" : "#EEF3F8", color: r.region === "NATIONAL" ? "#7A5A0E" : P.navy }}>
              {r.region}
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm truncate" style={{ color: P.navy }}>{r.name}</div>
              <div className="text-xs" style={{ color: P.dim }}>{r.coverage}</div>
            </div>
            <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`pub-sor-open-${r.region.toLowerCase()}`}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white rounded-sm shrink-0 hover:opacity-90"
              style={{ background: P.navy }}>
              <ExternalLink size={13} /> OPEN REGISTRY
            </a>
          </div>
        ))}
        {!regs.length && <div className="text-sm text-center py-6" style={{ color: P.dim }}>Loading registries…</div>}
      </div>
      <div className="border rounded-sm p-4 flex items-start gap-2.5 text-xs leading-relaxed"
        style={{ borderColor: "#E7D9B9", background: "#FBF6E9", color: "#7A5A0E" }}>
        <Fingerprint size={16} className="mt-0.5 shrink-0" />
        <span>
          A name alone is insufficient to positively identify a registrant — positive identification requires
          fingerprint verification. To report a registration violation, contact your local law enforcement agency.
          Misuse of registry information to harass or injure any person may subject you to criminal prosecution.
        </span>
      </div>
    </div>
  );
}
